import { ProjectInstance, SupabaseProjectInstance } from '@/types/projectTypes';
import { convertToProjectInstance } from '@/utils/dataConverters';
import { v4 as uuid } from 'uuid';
import { ProjectService, SceneService } from '@/modules/databaseService/types';
import type { IAnalyticsService } from '@/modules/analytics';
import {
  CONSUMED_TEMPLATES_CATEGORY,
  PUBLISHED_TEMPLATES_CATEGORY,
} from '@/modules/templateScenes/templateProjects';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { projectCreateBody, projectToLegacy, type LegacyProject } from '@/infrastructure/api/v1/mappers/projects';
import { refreshProjectSummary } from '@/modules/databaseService/projects';

export class ProjectAdapter implements ProjectService {
  constructor(
    private readonly scene: SceneService,
    private readonly analytics?: IAnalyticsService
  ) {}

  async fetchProjectsForUser(userId: string, includeTemplateProjects?: boolean): Promise<ProjectInstance[]> {
    // Published-template snapshots are never listed; My Templates only when asked for
    const excludeCategory = includeTemplateProjects
      ? [PUBLISHED_TEMPLATES_CATEGORY]
      : [CONSUMED_TEMPLATES_CATEGORY, PUBLISHED_TEMPLATES_CATEGORY];
    const { data, error } = await toResult(v1.GET('/api/v1/projects', {
      params: { query: { userUniqueId: userId, excludeCategory, sort: '-updatedAt', limit: 20, offset: 0 } },
    }));
    if (error) throw error;
    return data.items.map((dto) => convertToProjectInstance(projectToLegacy(dto) as unknown as SupabaseProjectInstance));
  }

  async addProjectInstanceRow(newProjectInstance: SupabaseProjectInstance): Promise<string> {
    return this.createProject(newProjectInstance as unknown as Partial<LegacyProject>);
  }

  // The owner and org are the caller's, set by the server (createProject)
  private async createProject(row: Partial<LegacyProject>): Promise<string> {
    try {
      const { data, error } = await toResult(v1.POST('/api/v1/projects', { body: projectCreateBody(row) }));

      if (error) {
        throw error;
      }

      return data.uniqueId;
    } catch (error) {
      console.error('Error adding project instance:', error);
      throw error;
    }
  }

  // The server marks the project published while it has a live version, along with its other summary fields
  async updateProjectPublishedStatus(projectId: string): Promise<any> {
    try {
      const { data, error } = await refreshProjectSummary(projectId);

      if (error) throw error;
      return [data];
    } catch (error) {
      console.error('Error updating project published status:', error);
      throw error;
    }
  }

  async createNewProject(userId: string, navigate: any, projectName: string): Promise<void> {
    try {
      const newProjectId = await this.createProject({
        project_instance_id: uuid(),
        user_id: userId,
        project_instance_display_name: projectName
      });
      
      if (!newProjectId) {
        console.error('Failed to create project: No project ID returned');
        return;
      }

      this.analytics?.trackLureoNewProjectCreated({ project_id: newProjectId });

      // Navigate to the project page instead of creating a scene
      navigate(`/project/${newProjectId}`);
      
    } catch (error) {
      console.error('Error creating project:', error);
    }
  }

  private getProjectInitials(projectName: string): string {
    return projectName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  /**
   * Deletes a project the caller owns with its scenes, stories, scene types and images, in one
   * server transaction (deleteProject). The owner is the caller.
   */
  async deleteProject(projectId: string): Promise<void> {
    const { error } = await toResult(v1.DELETE('/api/v1/projects/{projectUniqueId}', { params: { path: { projectUniqueId: projectId } } }));

    if (error) {
      throw error;
    }
  }
} 